/**
 * The fixed columns of the Report Items table, in display order.
 *
 * One list feeds the Material table, the print column picker, the Excel template and the Excel
 * importer, so a column cannot be labeled one way on screen and another way in an export.
 */
export interface ReportItemColumn {
  /** Column id used by the Material table and the print column selection. */
  columnKey: string;
  /** Header shown in the table, the print view and the Excel template. */
  label: string;
  /** Control name for this column in a row's FormGroup. */
  formKey: string;
  /** Character cap matching the cell input's `maxlength`; absent for non-text cells. */
  maxLength?: number;
  /** Extra header spellings the Excel importer accepts, beyond the label and both keys. */
  aliases?: string[];
}

export const REPORT_ITEM_COLUMNS: readonly ReportItemColumn[] = [
  { columnKey: 'kitLot', label: 'Kit Lot', formKey: 'lot', aliases: ['Kit Lot Number', 'Kit Lot #', 'Lot Number'] },
  { columnKey: 'assayName', label: 'Assay Name', formKey: 'assay', aliases: ['Assay'] },
  { columnKey: 'runId', label: 'Instrument ID', formKey: 'runId', aliases: ['Instrument', 'Instrument Serial Number'] },
  { columnKey: 'resultType', label: 'Result Type', formKey: 'resultType' },
  { columnKey: 'beadRegion', label: 'Bead Region', formKey: 'region', aliases: ['Region'] },
  { columnKey: 'beadCouplingDate', label: 'Bead Coupling Date', formKey: 'beadCouplingDate' },
  { columnKey: 'coupledBeadLn', label: 'Coupled Bead LN', formKey: 'coupledBeadLn', maxLength: 20 },
  { columnKey: 'dyedBeadLn', label: 'Dyed Bead LN', formKey: 'dyedBeadLn', maxLength: 20 },
  { columnKey: 'progenitorLn', label: 'Progenitor LN', formKey: 'progenitorLn', maxLength: 20 },
  { columnKey: 'antigenLn', label: 'Antigen LN', formKey: 'antigenLn', maxLength: 20 },
  { columnKey: 'antigenLoading', label: 'Antigen Loading', formKey: 'antigenLoadLn', maxLength: 20 },
  { columnKey: 'coupledBeadPn', label: 'Coupled Bead PN', formKey: 'coupledBeadPn', maxLength: 20 },
  { columnKey: 'ligandPn', label: 'Ligand PN', formKey: 'ligandPn', maxLength: 20 },
  { columnKey: 'beadReagentPn', label: 'Bead Reagent PN', formKey: 'beadReagentPn', maxLength: 20 },
  { columnKey: 'description', label: 'Description', formKey: 'description', maxLength: 100 }
];

/** The cap applied to an imported value for a panel's variable bulk-material columns. */
export const VARIABLE_COLUMN_MAX_LENGTH = 20;

/** Columns the app seeds onto a new row rather than collecting from the operator. */
const SEEDED_FORM_KEYS = new Set(['lot', 'runId', 'assay', 'resultType', 'region']);

/** The form keys a row collects from the operator, including the panel's variable columns. */
export const getOperatorEnteredFormKeys = (variableColumns: string[] = []): string[] => [
  ...REPORT_ITEM_COLUMNS.map(column => column.formKey).filter(key => !SEEDED_FORM_KEYS.has(key)),
  ...variableColumns
];

/**
 * Whether a row holds anything the operator supplied. Kit Lot, Instrument ID and Result Type are
 * seeded, and a seeded Bead Region is a negative sentinel, so a row carrying only those is still an
 * untouched placeholder and safe to drop without asking.
 */
export const hasOperatorEnteredData = (
  row: Record<string, unknown>,
  variableColumns: string[] = []
): boolean => {
  if (String(row['assay'] ?? '').trim() || Number(row['region']) > 0) {
    return true;
  }
  return getOperatorEnteredFormKeys(variableColumns)
    .some(key => row[key] !== null && row[key] !== undefined && row[key] !== '');
};

/** Column id to form control name, for reading a cell by the id the print selection carries. */
export const REPORT_ITEM_FORM_KEY_BY_COLUMN: Readonly<Record<string, string>> =
  Object.fromEntries(REPORT_ITEM_COLUMNS.map(column => [column.columnKey, column.formKey]));

/**
 * Strips case and punctuation from a header so "Kit Lot", "kit_lot" and "KitLot#" are one column.
 * Also lets a camelCase form key match its own label — `sampleDiluentLn` and "Sample Diluent LN"
 * both reduce to `samplediluentln`, which is what makes the panel's variable columns matchable
 * without a hand-written alias each.
 */
export const normalizeReportItemHeader = (value: string | null | undefined): string =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
